import {ValidationErrorKeys} from '@hconnect/common/consts'
import {removeMarkdownTags} from '@hconnect/common/utils'
import {type TFunction} from 'i18next'
import moment, {Moment} from 'moment-timezone'
import {object, string, date, number, boolean, array} from 'yup'

const TITLE_MAX_LENGTH = 300
const DESCRIPTION_MAX_LENGTH = 5000

const validateDateRange = (doNotStartBefore: any, dueDate: any): boolean => {
  if (!doNotStartBefore || !dueDate) {
    return true
  }
  const startDate = new Date(doNotStartBefore)
  const endDate = new Date(dueDate)
  if (startDate.toDateString() === endDate.toDateString()) {
    return true
  }
  return startDate <= endDate
}

export const getProcessWorkOrderValidationSchema = ({
  t,
  disableRepetition,
  originalDueDate,
  plantTimezone
}: {
  t: TFunction
  disableRepetition?: boolean
  originalDueDate?: Moment
  plantTimezone?: string
}) =>
  object().shape({
    title: string()
      .trim()
      .max(TITLE_MAX_LENGTH, t(ValidationErrorKeys.MAX_LENGTH, {max: TITLE_MAX_LENGTH}))
      .required(t(ValidationErrorKeys.REQUIRED)),
    description: richTextFieldValidatationSchema({t, max: DESCRIPTION_MAX_LENGTH}),
    plantId: string().required(t(ValidationErrorKeys.REQUIRED)),
    equipment: object().notRequired(),
    mainEquipment: object().notRequired(),
    processStage: string().notRequired(),
    status: string().required(t(ValidationErrorKeys.REQUIRED)),
    dueDate: date()
      // Allow keeping the original due date when editing
      // but prevent setting a new past date
      .test('min-date-validation', t('validation.cannotBeBeforeToday'), (value) => {
        if (!value) return true
        if (plantTimezone && originalDueDate) {
          const momentValue = moment.tz(value, plantTimezone)
          const momentNow = moment.tz(moment(), plantTimezone)
          return (
            momentValue.isSame(originalDueDate, 'day') ||
            momentValue.isSameOrAfter(momentNow, 'day')
          )
        }

        return moment(value).isSameOrAfter(moment(), 'day')
      })
      .required(t(ValidationErrorKeys.REQUIRED))
      .test('date-range-validation', t('validation.beforeDueDate'), function (value) {
        const doNotStartBefore = this.parent?.doNotStartBefore
        return validateDateRange(doNotStartBefore, value)
      }),
    doNotStartBefore: date().notRequired(),
    assignees: array().of(object()).notRequired(),
    isRecurring: boolean().notRequired(),
    repetitionInfo: object({
      startDate: date().when('repetition', {
        is: (val: string) => val === 'custom',
        then: (schema) => schema.required(t(ValidationErrorKeys.REQUIRED)),
        otherwise: (schema) => schema.notRequired()
      }),
      repetition: string().notRequired(),
      repeatEvery: number().when('repetition', {
        is: (val: string) => val === 'custom',
        then: (schema) =>
          schema
            .typeError(t(ValidationErrorKeys.REQUIRED))
            .required(t(ValidationErrorKeys.REQUIRED))
            .moreThan(0, t(ValidationErrorKeys.GREATER_THAN, {min: 0})),
        otherwise: (schema) => schema.notRequired()
      }),
      endAfterRepetitions: number()
        .nullable()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .when('repetition', {
          is: (val: string) => val === 'custom',
          then: (schema) =>
            schema.moreThan(0, t(ValidationErrorKeys.GREATER_THAN, {min: 0})).notRequired(),
          otherwise: (schema) => schema.notRequired()
        }),
      endDate: date().nullable().notRequired()
    })
      .test(
        'endAfterRepetitions-xor-endDate',
        t('validation.onlyOneOfEndAfterRepetitionsOrEndDate'),
        (value) => {
          if (!value) return true
          const {endAfterRepetitions, endDate} = value
          return !(endAfterRepetitions && endDate)
        }
      )
      .when('isRecurring', {
        is: true,
        then: (schema) => schema.required(t(ValidationErrorKeys.REQUIRED)),
        otherwise: (schema) => schema.nullable().optional()
      })
      .test(
        'repetition-required-when-recurring',
        t(ValidationErrorKeys.REQUIRED),
        function (value) {
          if (!value) return true
          // Skip validation if repetition is disabled
          if (disableRepetition) return true
          const {repetition} = value
          const isRecurring = this.parent.isRecurring
          if (isRecurring && !repetition) {
            return false
          }
          return true
        }
      )
  })

const richTextFieldValidatationSchema = ({t, max}: {t: TFunction; max: number}) =>
  string()
    .trim()
    .required(t(ValidationErrorKeys.REQUIRED))
    .test('max-length-without-markdown', t(ValidationErrorKeys.MAX_LENGTH, {max}), (value) => {
      if (!value) return false
      const plainText = removeMarkdownTags(value)
      return plainText.length <= max
    })
